import { useEffect, useRef, useCallback } from 'react';
import { useFileStatus } from '../context/FileStatusContext';
import { useProjectRefresh } from '@/hooks/useProjectRefresh';
import type { FilesTabRef } from '../tabs/FileExplorerTab';
import { castFileStatuses } from '@/common/utils/statusCastingUtils';
import type { FileStatus } from '@/hooks/useProjectSync';
import { FileSyncStatus } from '@/common/models/enums';

// Type for statuses that might come from Electron API with string status
type FileStatusWithStringStatus = {
  fileName: string;
  status: string | FileSyncStatus;
  isLocal: boolean;
};

interface UseSyncStatusRefreshProps {
  projectDir: string | FileSystemDirectoryHandle | null;
  latestVersionFiles: Array<{ fileName: string }>;
  checkStatus: () => Promise<
    | FileStatus[]
    | FileStatusWithStringStatus[]
    | Array<{ fileName: string; status: string; isLocal: boolean }>
    | null
  >;
  refetchLatestVersionData: () => Promise<void>;
  filesWithSyncRef?: React.RefObject<FilesTabRef | null>;
}

/**
 * Hook to manage automatic and manual sync status refresh
 * Handles status updates when directory or project data changes
 */
export function useSyncStatusRefresh({
  projectDir,
  latestVersionFiles,
  checkStatus,
  refetchLatestVersionData,
  filesWithSyncRef,
}: UseSyncStatusRefreshProps) {
  const { updateFileStatuses } = useFileStatus();
  const isRefreshingRef = useRef(false);

  // Use shared refresh hook
  const { refreshAll, isRefreshing } = useProjectRefresh({
    refetchLatestVersionData,
    filesWithSyncRef,
    checkStatus: async () => {
      const result = await checkStatus();
      return castFileStatuses(result);
    },
  });

  // Enhanced manual refresh function
  const handleManualRefresh = useCallback(async () => {
    if (!projectDir || latestVersionFiles.length === 0) return;

    try {
      const statuses = await refreshAll();
      if (statuses) {
        updateFileStatuses(castFileStatuses(statuses));
      }
    } catch (error) {
      console.error('Error during manual refresh:', error);
    }
  }, [projectDir, latestVersionFiles.length, refreshAll, updateFileStatuses]);

  // Refresh status when directory is selected or project data changes
  useEffect(() => {
    const refreshStatusInEffect = async (): Promise<void> => {
      if (!projectDir) {
        updateFileStatuses([]);
        return;
      }

      if (latestVersionFiles.length === 0) {
        return;
      }

      try {
        const statuses = await checkStatus();
        if (statuses && statuses.length > 0) {
          updateFileStatuses(castFileStatuses(statuses));
        } else {
          updateFileStatuses([]);
        }
      } catch (error) {
        console.error('Error in refreshStatusInEffect:', error);
        // Don't update statuses on error, keep previous state
      }
    };

    if (
      projectDir &&
      latestVersionFiles.length > 0 &&
      !isRefreshingRef.current
    ) {
      isRefreshingRef.current = true;
      refreshStatusInEffect().finally(() => {
        isRefreshingRef.current = false;
      });
    } else if (!projectDir || latestVersionFiles.length === 0) {
      updateFileStatuses([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDir, latestVersionFiles.length, updateFileStatuses]);

  return {
    handleManualRefresh,
    isRefreshing,
  };
}
